import { getAgeLimitError } from "../selectors/validation/getAgeLimitError.js";
import { getInvalidFormatErrors } from "../selectors/validation/getInvalidFormatErrors.js";
import { getIntervalError } from "../selectors/validation/getIntervalError.js";
import { getRenewAtError } from "../selectors/validation/getRenewAtError.js";
import { getPauseErrors } from "../selectors/validation/getPauseErrors.js";
import { validateExistingCustomerAndSubscription } from "../selectors/validation/validateExistingCustomerAndSubscription.js";
import { getErrorMessage, isNonEmptyString } from "../utils/importUtils.js";
import { getSkuValidationError } from "../selectors/validation/getSkuValidationError.js";
// Returns true if any of the provided fields already have errors
const hasAnyErrorsForFields = (validationErrors, fieldKeys) => fieldKeys.some((fieldKey) => (validationErrors[fieldKey]?.length ?? 0) > 0);
/**
 * Validates a list of imported contracts.
 * - Performs local field validation (format, duplicates, age, renewAt, pause, interval).
 * - Optionally performs remote checks against Crystallize (existing customer/subscription),
 *   but only if required fields exist and those fields have no prior local errors.
 * - Returns only rows that have any validation errors.
 */
export const validateContracts = async (contracts, client, periods, options) => {
    const MAX_CONCURRENT_LOOKUPS = 6;
    let activeLookups = 0;
    const waitQueue = [];
    let backoffMs = 0;
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const runWithConcurrencyAndBackoff = async (fn) => {
        if (activeLookups >= MAX_CONCURRENT_LOOKUPS) {
            await new Promise((resolve) => waitQueue.push(resolve));
        }
        activeLookups++;
        try {
            if (backoffMs > 0)
                await sleep(backoffMs);
            return await fn();
        }
        catch (error) {
            const message = getErrorMessage(error).toLowerCase();
            if (message.includes("429") ||
                message.includes("rate") ||
                message.includes("too many")) {
                backoffMs = Math.min(backoffMs + 150, 3000);
            }
            else {
                backoffMs = Math.max(backoffMs - 75, 0);
            }
            throw error;
        }
        finally {
            activeLookups--;
            const next = waitQueue.shift();
            if (next)
                next();
        }
    };
    const validationResults = await Promise.all(contracts.map(async (contract, rowIndex) => {
        let contractErrors = {};
        contractErrors = getInvalidFormatErrors(contract);
        contractErrors = getSkuValidationError(contract, contractErrors, options.validSkus);
        contractErrors = getAgeLimitError(contract, contractErrors);
        contractErrors = getRenewAtError(contract, contractErrors);
        contractErrors = getPauseErrors(contract, contractErrors);
        contractErrors = getIntervalError(contract, contractErrors, periods);
        const needsRemoteCheck = !!contract.phone_number &&
            !!contract.sku &&
            !hasAnyErrorsForFields(contractErrors, ["phone_number", "sku"]);
        if (needsRemoteCheck) {
            try {
                contractErrors = await runWithConcurrencyAndBackoff(() => validateExistingCustomerAndSubscription(contract, contractErrors, client));
            }
            catch (error) {
                const message = getErrorMessage(error);
                (contractErrors.phone_number ??= []).push(`Sjekk mot Crystallize feilet: ${message}`);
            }
        }
        if (Object.keys(contractErrors).length > 0) {
            const precomputed = options?.importIds?.[rowIndex];
            const importId = isNonEmptyString(precomputed)
                ? precomputed.trim()
                : `MISSING_IMPORT_ID_ROW_${rowIndex + 1}`;
            return {
                contractErrors,
                importId,
                rowIndex,
            };
        }
        return undefined;
    }));
    return validationResults.filter((result) => result !== undefined);
};
